/*
 * certificate-maker/index.js — the editor's controller.
 *
 * STATE IS THE DOCUMENT, NOT THE DOM
 * ──────────────────────────────────
 * Every control writes into `S` and asks for a redraw; nothing reads geometry
 * back out of the page. That is what lets one state produce a preview, a PDF, a
 * PNG and a saved project without four of them drifting.
 *
 * TWO WAYS IN, ONE MODEL
 * ──────────────────────
 * A field can be changed on the sheet (click it, type) or in the panel (find
 * it, type). Both write the SAME `S.texts` entry — there is no editor copy of
 * the text and no sync step, because there is nothing to sync. The predecessor
 * had the panel for words and the canvas for position, which is two mental
 * models for one object; this has one model with two doors.
 *
 * `S.nudge` and `S.blockStyle` are OFFSETS AND OVERRIDES, never absolutes. The
 * flow layout keeps running underneath, so a moved or resized block still
 * travels with the composition when the page size or the frame changes.
 * Absolute coordinates are why drag-and-drop editors fall apart the moment you
 * switch A4 landscape to portrait.
 */
(function () {
    'use strict';

    var CFG = window.__CERT__ || {};
    var T = CFG.t || {}, UI = CFG.ui || {};
    var SLUG = 'certificate-maker';

    function t(k, vars) {
        var s = T[k] || UI[k] || k;
        if (vars) for (var v in vars) s = String(s).replace(new RegExp(':' + v, 'g'), vars[v]);
        return s;
    }
    function $(id) { return document.getElementById(id); }
    function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
    function esc(s) { var d = document.createElement('div'); d.textContent = String(s == null ? '' : s); return d.innerHTML; }

    /* ── state ───────────────────────────────────────────────────────────── */

    var S = {
        type: 'gramota',
        preset: 'gramota-classic',
        page: null,                 // null → the preset's own page
        texts: {},
        style: {},                  // document-wide overrides on top of the preset
        blockStyle: {},             // per-element type overrides: {size, color, align, font}
        nudge: {},                  // blockId → {dx, dy}
        overlays: [],
        batch: { on: false, raw: '', header: false, kase: 'nomn', num: false,
                 numPrefix: '№ ', numStart: 1, numPad: 3, index: 0 },
        out: { bleed: 0, crop: false, ruler: false, dpi: 300, scale: 0, dx: 0, dy: 0 },
        overprint: false, blankForm: false,
        backdrop: null, backdropOp: 45,
        safe: false
    };

    var lastResult = null, lastRows = null, ovSeq = 0, pendingOv = null, _fioDead = false;

    /* ── the background picture lives OUT of the state ───────────────────────
     * `bgRawSrc` is the untouched upload, `bgBaked` the adjusted copy the sheet
     * actually draws. Neither is in `S.style`, and that is the point: the
     * editor serialises the state on every history step, sixty deep, so a
     * 700 KB data URL in each of them is forty megabytes of strings for the
     * privilege of undoing a slider. The style carries the four ADJUSTMENTS;
     * the pixels are rebuilt from the original whenever they change. */
    var bgRawSrc = null;
    var bgBaked = null, bgBakeSeq = 0, bgBakeTimer = 0;
    var editor = null, measure = null, tab = 'design';
    var T0 = Date.now();

    /* ── fonts ───────────────────────────────────────────────────────────── */

    function cssFor(id) {
        var f = window.CertTemplates.font(id);
        return (f ? '"' + f.css + '", ' : '') + window.CertRender.FALLBACK;
    }
    function presetOf() { return window.CertTemplates.preset(S.preset) || window.CertTemplates.PRESETS[0]; }

    /* ── document assembly ───────────────────────────────────────────────── */

    function textsFor(rowIndex) {
        var out = {}, rows = lastRows, ctx = null;
        if (S.batch.on && rows && rows.rows.length) {
            var i = Math.max(0, Math.min(rowIndex || 0, rows.rows.length - 1));
            ctx = window.CertData.rowContext(rows.rows[i], rows.headers, i, {
                '№': S.batch.num ? seqAt(i) : String(i + 1)
            });
            if (S.batch.fixCaps) ctx = decapsContext(ctx);
            if (S.batch.num) ctx.num = seqAt(i);
        } else {
            /* One sheet still has a recipient — the name typed in the field — so
               gender forks work there too. Skipped when that field is itself a
               token, which would otherwise resolve against its own text. */
            var raw = String(S.texts.name || '');
            if (raw && !/\{[^}]+\}/.test(raw)) ctx = { fio: raw, 'фио': raw, name: raw, 'имя': raw };
        }
        var kase = S.batch.on ? S.batch.kase : 'nomn';
        Object.keys(S.texts).forEach(function (k) {
            var v = S.texts[k];
            out[k] = ctx ? window.CertData.fill(v, ctx, {
                defaultCase: kase, nameKeys: ['fio', 'фио', 'имя', 'name', '1']
            }).text : v;
        });
        return out;
    }

    /* «КОВАЛЬЧУК ДМИТРИЙ» → «Ковальчук Дмитрий». Applied to the ROW data, not
       to the template, so a heading the author deliberately typed in capitals
       stays as typed. Only words that are entirely upper case are touched, so
       «МБОУ» inside a cell survives — an acronym is not shouting. */
    function decapsContext(ctx) {
        var out = {};
        Object.keys(ctx).forEach(function (k) {
            var v = ctx[k];
            out[k] = (typeof v === 'string') ? decaps(v) : v;
        });
        return out;
    }
    function decaps(str) {
        return String(str).replace(/[^\s]+/g, function (w) {
            var up = w.toLocaleUpperCase('ru');
            if (w !== up || w.length < 4) return w;             // not shouting, or an acronym
            if (!/[a-zа-яё]/i.test(w)) return w;                // digits and punctuation
            return w.charAt(0) + w.slice(1).toLocaleLowerCase('ru');
        });
    }

    /** True when the list actually contains an ALL-CAPS word worth fixing. */
    function listHasCaps() {
        if (!lastRows || !lastRows.rows.length) return false;
        return lastRows.rows.some(function (r) {
            return (r || []).some(function (cell) { return decaps(cell) !== String(cell); });
        });
    }

    function seqAt(i) {
        return window.CertData.seq(i, {
            prefix: S.batch.numPrefix, start: +S.batch.numStart || 1, step: 1, pad: +S.batch.numPad || 0
        });
    }

    function docFor(rowIndex) {
        var opts = {};
        if (S.page) opts.page = S.page;
        ['frame', 'ink', 'accent', 'paper', 'vAlign', 'pad', 'wm'].forEach(function (k) {
            if (S.style[k] !== undefined) opts[k] = S.style[k];
        });
        if (S.blankForm) opts.blankLines = true;
        var doc = window.CertTemplates.makeDoc(S.preset, textsFor(rowIndex), opts);

        /* Overprint: the bought blank already carries the frame, the paper and
         * the ornament, so all three come off. Not a preset switch — the author
         * keeps whatever typography they chose. */
        if (S.overprint) {
            doc.style.frame = 'none';
            doc.style.paper = 'none';
            doc.style.watermark.on = false;
            if (S.style.frameInset == null) doc.style.frameInset = 10;
        }
        if (S.style.frameInset != null) doc.style.frameInset = S.style.frameInset;
        if (S.style.wmSize != null) doc.style.watermark.size = S.style.wmSize / 100;
        if (S.style.wmOp != null) doc.style.watermark.opacity = S.style.wmOp / 100;

        /* The background layer. ⚠️ It has to be copied ACROSS explicitly, like
           everything else here: makeDoc() builds the style from the preset and
           the short list above, so a key the list does not name simply never
           reaches layout. The picker looked as though it worked — the thumbnail
           highlighted, the state changed, undo recorded it — and the sheet drew
           the same 25 nodes every time. */
        ['bg', 'bgImageAR', 'bgImageLuma', 'bgImageOpacity', 'bgInset', 'bgStrength', 'scrim']
            .forEach(function (k) {
                if (S.style[k] !== undefined) doc.style[k] = S.style[k];
            });
        if (bgBaked) doc.style.bgImage = bgBaked;
        /* Overprint prints onto a blank that already carries its own art: ours
           would print ON TOP of the bought one. */
        if (S.overprint) { doc.style.bg = 'none'; delete doc.style.bgImage; }

        /* Document-wide font roles. */
        ['title', 'name', 'body'].forEach(function (role) {
            var f = S.style['font_' + role];
            if (!f) return;
            doc.blocks.forEach(function (b) {
                if (role === 'title' && b.id === 'title') b.font = f;
                if (role === 'name' && b.id === 'name') b.font = f;
                if (role === 'body' && b.id !== 'title' && b.id !== 'name') {
                    if (b.kind === 'row') (b.cells || []).forEach(function (c) { c.font = f; });
                    else b.font = f;
                }
            });
        });

        /* Per-element overrides last: an explicit choice on one line beats the
         * document-wide role that produced it. */
        doc.blocks.forEach(function (b) {
            applyBlockStyle(b, S.blockStyle[b.id]);
            var n = S.nudge[b.id];
            if (n) { b.dx = n.dx; b.dy = n.dy; }
            if (b.kind === 'row') (b.cells || []).forEach(function (c) { applyBlockStyle(c, S.blockStyle[c.id]); });
        });

        doc.overlays = S.overlays.map(function (o) {
            var c = {};
            for (var k in o) c[k] = o[k];
            return c;
        });
        return doc;
    }

    function applyBlockStyle(b, st) {
        if (!st) return;
        if (st.size != null && b.size) b.size = b.size * st.size;
        if (st.font) b.font = st.font;
        if (st.color) b.color = st.color;
        if (st.align) b.align = st.align;
        if (st.bold != null) b.bold = st.bold;
        if (st.hidden) b.on = false;
    }

    function buildOne(rowIndex) {
        return window.CertLayout.build(docFor(rowIndex), measure, { frames: window.CertFrames, backgrounds: window.CertBackgrounds });
    }

    /* ── rendering ───────────────────────────────────────────────────────── */

    var rafId = 0;
    function schedule() {
        if (rafId) return;
        rafId = requestAnimationFrame(function () { rafId = 0; redraw(); });
    }

    /* The paper rectangle, in CSS pixels: the largest box with the page's
     * aspect ratio that fits the stage. Recomputed on every draw because the
     * page size can change under it, and on resize because the stage can. */
    function sizeSheet() {
        if (!lastResult) return;
        var wrap = $('cm-wrap'), sheet = $('cm-sheet'), stage = document.querySelector('.cm-stage');
        if (!wrap || !sheet) return;
        var cs = getComputedStyle(wrap);
        var padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
        var availW = wrap.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
        var availH = wrap.clientHeight - padY;
        if (!(availW > 0)) return;
        var ratio = lastResult.page.w / lastResult.page.h;
        /* Which constraint applies is a LAYOUT fact, and CSS is the only thing
           that knows which layout is in force — see --cm-fit in the blade. */
        var peek = stage && getComputedStyle(stage).getPropertyValue('--cm-peek-on').trim() === '1';
        var w;
        if (peek) {
            /* Phone: how tall the stage is, is OURS to decide.
               ⚠️ Derive it from the WIDTH and the visible viewport — never from
               the box we are about to resize. Reading clientHeight back here is
               a feedback loop: the stage shrinks to the sheet, the sheet then
               shrinks to the stage, and the certificate walks to nothing.
               visualViewport, not innerHeight, so the peek gives way to the
               on-screen keyboard instead of pushing the field being typed in
               off the bottom. */
            var vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight || 640;
            var status = stage.querySelector('.cm-status');
            /* Budget the WHOLE stage, then pay the status bar out of it. */
            var budget = Math.max(150, Math.min(vh * 0.42, 460));
            var hMax = Math.max(90, budget - (status ? status.offsetHeight : 0) - padY);
            var h = Math.min(availW / ratio, hMax);
            w = h * ratio;
            var shell = document.querySelector('.cm-shell') || stage;
            shell.style.setProperty('--cm-peek', Math.round(h + padY) + 'px');
        } else {
            var fits = cs.getPropertyValue('--cm-fit').trim() === '1';
            w = (fits && availH > 40) ? Math.min(availW, availH * ratio) : availW;
        }
        sheet.style.width = Math.max(80, Math.floor(w)) + 'px';
        sheet.style.height = Math.max(56, Math.floor(w / ratio)) + 'px';
    }

    function redraw() {
        if (!measure) return;
        lastResult = buildOne(S.batch.on ? S.batch.index : 0);
        sizeSheet();
        window.CertRender.draw($('cm-svg'), lastResult, {
            cssFor: cssFor, interactive: true, showSafe: S.safe,
            backdrop: S.backdrop, backdropOpacity: S.backdropOp / 100
        });
        if (editor) { editor.repaint(); positionFloat(); }
        paintChecks();
        paintPager();
        save();
    }

    /** Characters no embedded face can draw. `planRuns` reports the face it
     *  chose per run and `null` when it found none — that null is the whole
     *  signal, and it is the difference between a certificate with a name on it
     *  and one with a gap. */
    function uncoveredChars(text) {
        if (!window.PdfTextFont || !window.PdfTextFont.planRuns) return [];
        var out = [];
        try {
            window.PdfTextFont.planRuns(String(text || ''), 'noto').forEach(function (r) {
                if (r.faceId) return;
                Array.from(r.text).forEach(function (c) {
                    if (c.trim() && out.indexOf(c) < 0) out.push(c);
                });
            });
        } catch (e) { return []; }
        return out;
    }

    /* ── preflight ───────────────────────────────────────────────────────── */

    function runChecks() {
        if (!lastResult || !window.CertSmart) return [];
        try {
            return window.CertSmart.preflight(lastResult, docFor(S.batch.on ? S.batch.index : 0), {
                uncovered: uncoveredChars
            });
        }
        catch (e) { return []; }
    }

    function paintChecks() {
        var list = runChecks();
        var stops = list.filter(function (c) { return c.level === 'stop'; }).length;
        var warns = list.length - stops;
        var badge = $('cm-checkBadge');
        if (badge) {
            badge.textContent = stops || warns || '✓';
            badge.className = 'cm-badge' + (stops ? '' : warns ? '' : ' ok');
            badge.style.background = stops ? 'var(--danger)' : warns ? 'var(--warning)' : '';
            badge.style.color = stops ? '#fff' : '';
        }
        var box = $('cm-checkList');
        if (!box) return;
        if (!list.length) {
            box.innerHTML = '<div class="cm-h">' + esc(UI.preflight) + '</div>'
                + '<ul class="cm-checks"><li><span class="ok">✓</span>' + esc(t('check_all_ok')) + '</li></ul>';
            return;
        }
        var items = list.map(function (c) {
            var msg = t('check_' + c.code, { value: c.value, text: cut(c.text || ''), block: labelOf(c.block) });
            return '<li><span class="' + c.level + '">' + (c.level === 'stop' ? '●' : '▲') + '</span><span>' + esc(msg) + '</span></li>';
        });
        box.innerHTML = '<div class="cm-h">' + esc(UI.preflight) + '</div><ul class="cm-checks">' + items.join('') + '</ul>';
    }
    function cut(s) { s = String(s || ''); return s.length > 40 ? s.slice(0, 38) + '…' : s; }

    function paintPager() {
        var wrap = $('cm-pager');
        var n = S.batch.on && lastRows ? lastRows.rows.length : 0;
        wrap.hidden = n < 2;
        if (n < 2) return;
        $('cm-pageLabel').textContent = (S.batch.index + 1) + ' ' + t('batch_of', { n: n });
        $('cm-prev').disabled = S.batch.index <= 0;
        $('cm-next').disabled = S.batch.index >= n - 1;
    }

    /* ── the contextual toolbar ──────────────────────────────────────────── */

    function labelOf(id) {
        if (!id) return '';
        return UI['f_' + String(id).toLowerCase()] || UI[String(id).toLowerCase()] || id;
    }

    function fieldOf(id) {
        /* Block ids ARE field names by construction — except the foot cells,
         * which carry two fields between them. */
        if (id === 'place') return 'place';
        if (id === 'sign') return 'role';
        return S.texts[id] !== undefined || ['org', 'title', 'subtitle', 'lead', 'name', 'detail', 'reason', 'note', 'number'].indexOf(id) >= 0 ? id : null;
    }

    /* The place cell prints «г. Москва, «25» мая 2026 г.» — one line built from
     * two fields. Editing it by hand collapses the pair into the place field:
     * what you typed is what it says, and the date picker stops fighting you. */
    function beforeEdit(id) {
        if (id !== 'place') return;
        var d = (S.texts.date || '').trim();
        if (!d) return;
        S.texts.place = [S.texts.place, d].filter(Boolean).join(', ');
        S.texts.date = '';
        syncTextInputs();
    }

    function positionFloat() {
        var f = $('cm-float');
        var id = editor && editor.selected();
        if (!id) { f.hidden = true; return; }
        var box = editor.box(id);
        if (!box) { f.hidden = true; return; }
        var svg = $('cm-svg'), wrap = $('cm-wrap');
        var r = svg.getBoundingClientRect(), wr = wrap.getBoundingClientRect();
        var k = r.width / lastResult.page.w;
        f.hidden = false;
        /* Docked at the bottom of the stage on a phone — see --cm-dock. Nothing
           to compute, and computing anyway would fight the stylesheet. */
        if (getComputedStyle(f).getPropertyValue('--cm-dock').trim() === '1') {
            f.style.left = ''; f.style.top = '';
            return;
        }
        var fw = f.offsetWidth || 240;
        var left = (r.left - wr.left) + (box.x + box.w / 2) * k - fw / 2;
        var top = (r.top - wr.top) + box.y * k - f.offsetHeight - 9;
        /* Above the selection, unless there is no room — then below it, so the
           toolbar never leaves the stage or covers the site header. */
        if (top < 4) top = (r.top - wr.top) + (box.y + box.h) * k + 9;
        f.style.left = Math.max(6, Math.min(wr.width - fw - 6, left)) + 'px';
        f.style.top = Math.max(4, top) + 'px';
    }

    function buildFloat(id) {
        var f = $('cm-float');
        f.innerHTML = '';
        if (!id) { f.hidden = true; return; }
        var ov = overlayById(id);

        function btn(html, title, on, pressed) {
            var b = document.createElement('button');
            b.type = 'button'; b.className = 'cm-btn ghost' + (pressed ? ' on' : '');
            b.innerHTML = html; b.title = title || ''; b.setAttribute('aria-label', title || '');
            b.addEventListener('click', on);
            f.appendChild(b);
            return b;
        }
        function sep() {
            var s = document.createElement('span');
            s.className = 'cm-head-sep'; s.setAttribute('aria-hidden', 'true');
            f.appendChild(s);
        }

        if (ov) {
            btn('−', t('smaller'), function () { sizeOverlay(ov, 0.9); });
            btn('+', t('bigger'), function () { sizeOverlay(ov, 1.1); });
            sep();
            if (ov.kind === 'image') {
                btn('⟲', t('rotate'), function () { ov.rot = ((ov.rot || 0) - 5); commitAfter('rotate'); });
                btn('⟳', t('rotate'), function () { ov.rot = ((ov.rot || 0) + 5); commitAfter('rotate'); });
                sep();
            }
            btn('🗑️', UI.remove, function () {
                S.overlays = S.overlays.filter(function (o) { return o !== ov; });
                editor.select(null); paintOverlays(); commitAfter('overlay_remove');
            });
            f.hidden = false;
            return;
        }

        var st = S.blockStyle[id] || (S.blockStyle[id] = {});
        /* Font. Only for a text block, and only faces that can set the text. */
        var selEl = document.createElement('select');
        selEl.className = 'cm-sel';
        selEl.setAttribute('aria-label', UI.font_body);
        window.CertTemplates.FONTS.forEach(function (fo) {
            var o = document.createElement('option');
            o.value = fo.id; o.textContent = fo.css.replace(/^CM /, '');
            o.style.fontFamily = cssFor(fo.id);
            selEl.appendChild(o);
        });
        selEl.value = st.font || currentFontOf(id);
        selEl.addEventListener('change', function () { st.font = selEl.value; commitAfter('font'); });
        f.appendChild(selEl);

        btn('−', t('smaller'), function () { st.size = clamp((st.size || 1) * 0.92, 0.4, 3); commitAfter('size'); });
        btn('+', t('bigger'), function () { st.size = clamp((st.size || 1) * 1.09, 0.4, 3); commitAfter('size'); });
        sep();

        var sw = document.createElement('label');
        sw.className = 'cm-swatch'; sw.title = UI.ink;
        var ci = document.createElement('input');
        ci.type = 'color';
        ci.value = colorHexOf(id);
        ci.addEventListener('input', function () { st.color = ci.value; schedule(); });
        ci.addEventListener('change', function () { commitAfter('colour'); });
        sw.appendChild(ci);
        sw.style.background = ci.value;
        f.appendChild(sw);
        sep();

        [['align-l', 'left'], ['align-c', 'center'], ['align-r', 'right']].forEach(function (a) {
            btn(iconSvg(a[0]), UI['va_' + a[1]] || a[1], function () {
                st.align = a[1]; commitAfter('align');
            }, (st.align || currentAlignOf(id)) === a[1]);
        });
        sep();
        btn(iconSvg('eye'), t('hide_block'), function () {
            st.hidden = true; editor.select(null); commitAfter('hide');
        });
        f.hidden = false;
    }

    function iconSvg(n) {
        var d = {
            'align-l': '<path d="M4 6h16M4 12h9M4 18h13"/>',
            'align-c': '<path d="M4 6h16M8 12h8M6 18h12"/>',
            'align-r': '<path d="M4 6h16M11 12h9M7 18h13"/>',
            'eye': '<path d="M3 3l18 18"/><path d="M10.6 6.2A9 9 0 0 1 12 6c6 0 10 6 10 6a17 17 0 0 1-3.2 3.6M6.5 8.5A17 17 0 0 0 2 12s4 6 10 6a9 9 0 0 0 3.3-.6"/>'
        }[n] || '';
        return '<svg class="cm-i" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + d + '</svg>';
    }

    function currentFontOf(id) {
        var p = lastResult && lastResult.prims.filter(function (q) { return q.type === 'text' && q.blockId === id; })[0];
        return p ? p.font : presetOf().body;
    }
    function currentAlignOf(id) {
        var p = lastResult && lastResult.prims.filter(function (q) { return q.type === 'text' && q.blockId === id; })[0];
        return p ? (p.anchor === 'start' ? 'left' : p.anchor === 'end' ? 'right' : 'center') : 'center';
    }
    function colorHexOf(id) {
        var p = lastResult && lastResult.prims.filter(function (q) { return q.type === 'text' && q.blockId === id; })[0];
        return (p && /^#[0-9a-f]{6}$/i.test(p.color)) ? p.color : '#1d2b45';
    }
    function overlayById(id) { return S.overlays.filter(function (o) { return o.id === id; })[0]; }
    function sizeOverlay(ov, k) {
        var w = clamp(ov.w * k, 6, 200);
        if (ov.h) ov.h *= w / ov.w;
        ov.w = w;
        commitAfter('overlay_size');
    }
    function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
    /* Set by the first TRUSTED input inside the shell — see `watchForUser`. */
    var userActed = false;

    function commitAfter(reason) {
        schedule(); if (editor) editor.commit(reason);
        /* Only a human's change counts. Startup applies a preset through this
           very function, so keying off the reason would call every visitor an
           editor — which is exactly what it used to do. */
        if (userActed) { stageOnce('edited'); warmExportFonts(); }
        fun(reason, {});
    }

    /** Flips `userActed` the first time a person touches the tool.
     *
     * Capture phase, so a handler that stops propagation cannot hide the fact,
     * and `isTrusted` so our own synthetic events (the preset applied at
     * startup dispatches `change` on several controls) do not count. */
    function watchForUser() {
        var shell = document.querySelector('.cm-shell');
        if (!shell) return;
        var mark = function (ev) {
            if (!ev.isTrusted || userActed) return;
            userActed = true;
            /* Warm here rather than only from commitAfter: the canvas gestures
               (select, drag, nudge) go through the editor's own history and
               never reach that function, so hanging the warm-up off it alone
               left the export cold for exactly the users who edit most. */
            warmExportFonts();
            ['pointerdown', 'keydown', 'change', 'input'].forEach(function (t) {
                shell.removeEventListener(t, mark, true);
            });
        };
        ['pointerdown', 'keydown', 'change', 'input'].forEach(function (t) {
            shell.addEventListener(t, mark, true);
        });
    }

    /* The TTFs the exporter will need, fetched while the user is still working.
     *
     * Only the faces actually on the sheet, only once each, and only after a
     * first edit — a visitor who looks and leaves downloads none of them. Plain
     * `fetch` rather than a preload link: the goal is the HTTP cache entry that
     * pdf-text-font's own request will hit, and a failure here must change
     * nothing, so every error is swallowed. */
    var _warmed = {};
    function warmExportFonts() {
        var P = window.PdfTextFont;
        /* `faceMeta`, never `face`: the latter DOWNLOADS the font — which is how
           this warm-up first managed to pull 556 KB of Noto on page load while
           reporting that it had warmed nothing. */
        if (!P || !P.faceMeta || !P.load || _warming) return;
        _warming = 1;
        /* The coverage table (16 KB of JSON) has to arrive before any face can
           be located; it is lazy, and without this the warm-up silently found
           an empty table and warmed nothing — measured: export 910 ms warm,
           5359 ms cold. */
        P.load().then(warmNow).catch(function () {});
    }

    var _warming = 0;
    function warmNow() {
        var P = window.PdfTextFont;
        /* Per face, whether the sheet uses it at all and whether it uses BOLD.
           Warming both weights unconditionally cost 556 KB of Noto-Bold that no
           default document embeds. */
        var ids = {};
        (lastResult && lastResult.prims || []).forEach(function (p) {
            if (p.type !== 'text' || !p.font) return;
            ids[p.font] = ids[p.font] || { bold: false };
            if (p.bold) ids[p.font].bold = true;
        });
        ids.noto = ids.noto || { bold: false };   /* the fallback is embedded almost always */
        Object.keys(ids).forEach(function (id) {
            var f;
            try { f = P.faceMeta(id); } catch (e) { return; }
            if (!f || !f.url) return;
            [f.url, ids[id].bold ? f.boldUrl : null].forEach(function (u) {
                if (!u || _warmed[u]) return;
                _warmed[u] = 1;
                try { fetch(u, { credentials: 'same-origin', priority: 'low' }).catch(function () {}); }
                catch (e) {}
            });
        });
    }

    /* ── the selection pane ──────────────────────────────────────────────── */

    function paintSelection(id) {
        var box = $('cm-selbox');
        if (!id) { box.hidden = true; return; }
        box.hidden = false;
        $('cm-selName').textContent = labelOf(id);
        var body = $('cm-selBody');
        body.innerHTML = '';
        var ov = overlayById(id);

        if (ov) {
            body.appendChild(range(UI.size_mm, 8, 120, Math.round(ov.w), function (v) {
                var k = v / ov.w; ov.w = v; if (ov.h) ov.h *= k; schedule();
            }, function () { editor.commit('overlay_size'); }));
            body.appendChild(range(UI.opacity, 20, 100, Math.round((ov.opacity == null ? 1 : ov.opacity) * 100), function (v) {
                ov.opacity = v / 100; schedule();
            }, function () { editor.commit('overlay_opacity'); }));
            if (ov.kind === 'image') {
                body.appendChild(range(UI.rotate, -30, 30, ov.rot || 0, function (v) { ov.rot = v; schedule(); },
                    function () { editor.commit('overlay_rotate'); }));
            }
            return;
        }

        var field = fieldOf(id);
        if (field) {
            var lab = document.createElement('label');
            lab.className = 'cm-field';
            lab.innerHTML = '<span class="cm-flabel">' + esc(UI.sel_text) + '</span>';
            var ta = document.createElement('textarea');
            ta.className = 'cm-input'; ta.rows = 2;
            ta.value = S.texts[field] || '';
            ta.addEventListener('input', function () { S.texts[field] = ta.value; syncTextInputs(); schedule(); });
            ta.addEventListener('change', function () { editor.commit('type'); });
            lab.appendChild(ta);
            body.appendChild(lab);
        }

        var st = S.blockStyle[id] || (S.blockStyle[id] = {});
        body.appendChild(range(UI.sel_size, 40, 250, Math.round((st.size || 1) * 100), function (v) {
            st.size = v / 100; schedule();
        }, function () { editor.commit('size'); }));

        var pos = document.createElement('p');
        pos.className = 'cm-hint';
        var n = S.nudge[id];
        pos.textContent = n && (n.dx || n.dy)
            ? t('sel_moved', { dx: n.dx, dy: n.dy })
            : t('sel_flow');
        body.appendChild(pos);

        if (n && (n.dx || n.dy)) {
            var reset = document.createElement('button');
            reset.type = 'button'; reset.className = 'cm-link';
            reset.textContent = t('sel_reset_pos');
            reset.addEventListener('click', function () {
                delete S.nudge[id]; commitAfter('reset_pos'); paintSelection(id);
            });
            body.appendChild(reset);
        }
        if (st.hidden) {
            var show = document.createElement('button');
            show.type = 'button'; show.className = 'cm-link';
            show.textContent = t('show_block');
            show.addEventListener('click', function () { st.hidden = false; commitAfter('show'); });
            body.appendChild(show);
        }
    }

    function range(label, min, max, value, onInput, onDone) {
        var l = document.createElement('label');
        l.className = 'cm-field';
        l.innerHTML = '<span class="cm-flabel">' + esc(label) + '</span>';
        var r = document.createElement('input');
        r.type = 'range'; r.min = min; r.max = max; r.value = value;
        r.className = 'cm-input'; r.style.padding = '0';
        r.addEventListener('input', function () { onInput(+r.value); });
        if (onDone) r.addEventListener('change', onDone);
        l.appendChild(r);
        return l;
    }

    /* ── pickers ─────────────────────────────────────────────────────────── */

    function buildTypeSelect() {
        var sel = $('cm-type');
        sel.innerHTML = '';
        window.CertTemplates.TYPES.forEach(function (ty) {
            var o = document.createElement('option');
            o.value = ty; o.textContent = (CFG.types || {})[ty] || ty;
            sel.appendChild(o);
        });
        sel.value = S.type;
    }

    function thumbSvg(result, w) {
        var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', w);
        window.CertRender.draw(svg, result, { cssFor: cssFor });
        return svg;
    }

    function buildPresetThumbs() {
        var box = $('cm-presets');
        box.innerHTML = '';
        window.CertTemplates.PRESETS.filter(function (p) {
            return p.type === S.type || p.id === 'overprint';
        }).forEach(function (p) {
            var b = document.createElement('button');
            b.type = 'button'; b.className = 'cm-thumb';
            b.setAttribute('aria-pressed', p.id === S.preset ? 'true' : 'false');
            var sample = (CFG.samples || {})[p.type] || {};
            var doc = window.CertTemplates.makeDoc(p.id, sample);
            b.appendChild(thumbSvg(window.CertLayout.build(doc, measure, { frames: window.CertFrames, backgrounds: window.CertBackgrounds }), 110));
            var s = document.createElement('span');
            s.textContent = (CFG.presets || {})[p.id] || p.id;
            b.appendChild(s);
            b.addEventListener('click', function () { applyPreset(p.id, true); });
            box.appendChild(b);
        });
    }

    /* ⚠️ A border thumbnail must NOT be the A4 sheet shrunk. At 88 px for a
     * 297 mm page the scale is 0.3 px/mm, so a 1.1 mm rule renders as a third of
     * a pixel and half the ornaments looked like empty rectangles. Drawing the
     * SAME builder at a card-sized page puts it at ~1.3 px/mm. */
    var THUMB_W = 105, THUMB_H = 74;
    function frameThumbSvg(id, ink, accent, px) {
        var built = window.CertFrames.build(id, { w: THUMB_W, h: THUMB_H, ink: ink, accent: accent });
        var res = {
            page: { w: THUMB_W, h: THUMB_H },
            prims: [{ type: 'rect', x: 0, y: 0, w: THUMB_W, h: THUMB_H, fill: '#fffdf7', color: null, sw: 0 }].concat(built.prims),
            blocks: []
        };
        var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', px);
        window.CertRender.draw(svg, res, { cssFor: cssFor });
        return svg;
    }

    /* ── background ──────────────────────────────────────────────────────────
     * One layer, two sources: art built for this page, or a picture the author
     * brought. The picker below treats them as one choice, because to the reader
     * they are one — «что за спиной у текста».
     */
    function bgThumbSvg(id, ink, accent, px) {
        var built = window.CertBackgrounds.build(id, {
            w: THUMB_W, h: THUMB_H, ink: ink, accent: accent, frames: window.CertFrames
        });
        var res = {
            page: { w: THUMB_W, h: THUMB_H },
            prims: [{ type: 'rect', x: 0, y: 0, w: THUMB_W, h: THUMB_H, fill: '#fffdf7', color: null, sw: 0 }]
                .concat(built.prims),
            blocks: []
        };
        var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', px);
        window.CertRender.draw(svg, res, { cssFor: cssFor });
        return svg;
    }

    function buildBgThumbs() {
        var box = $('cm-bgs');
        if (!box || !window.CertBackgrounds) return;
        box.innerHTML = '';
        var custom = !!bgRawSrc;
        var cur = custom ? null : (S.style.bg || 'none');
        var pal = window.CertTemplates.palette(presetOf().pal) || {};
        var ink = S.style.ink || pal.ink, accent = S.style.accent || pal.accent;
        window.CertBackgrounds.list().forEach(function (id) {
            var b = document.createElement('button');
            b.type = 'button'; b.className = 'cm-thumb';
            b.setAttribute('aria-pressed', id === cur ? 'true' : 'false');
            b.appendChild(bgThumbSvg(id, ink, accent, 120));
            var s = document.createElement('span');
            s.textContent = (CFG.bgs || {})[id] || id;
            b.appendChild(s);
            b.addEventListener('click', function () { pickBackground(id); });
            box.appendChild(b);
        });
    }

    function pickBackground(id) {
        S.style.bg = id;
        /* Choosing built-in art drops a picture that was there: they are the
           same layer, and leaving both would mean the choice did nothing. */
        if (bgRawSrc) clearBgImage(true);
        /* The art already frames the page. If the author has not picked a frame
           themselves, a second border on top reads as two templates stacked —
           so the inferred frame steps aside, and steps back when the background
           does. Once they DO pick one it is theirs and nothing touches it, the
           same rule as the batch header flag. */
        if (!S.style.frameTouched) {
            if (id !== 'none') { S.style.frame = 'none'; }
            else { delete S.style.frame; }
            buildFrameThumbs();
        }
        syncBgUi();
        buildBgThumbs();
        commitAfter('background');
    }

    function clearBgImage(quiet) {
        bgRawSrc = null; bgBaked = null; bgBakeSeq++;
        delete S.style.bgImageAR; delete S.style.bgImageLuma;
        delete S.style.bgGray; delete S.style.bgBlur; delete S.style.bgTint;
        var f = $('cm-bgFile'); if (f) f.value = '';
        if (!quiet) { syncBgUi(); buildBgThumbs(); commitAfter('background'); }
    }

    function readBgImage(file) {
        if (!file) return;
        if (file.size > 12 * 1024 * 1024) { toast(t('err_big'), 'error'); return; }
        var fr = new FileReader();
        fr.onload = function () {
            window.CertBgImage.prepare(fr.result).then(function (prepped) {
                bgRawSrc = prepped.src;
                S.style.bgImageAR = prepped.ar;
                S.style.bgGray = 0; S.style.bgBlur = 0; S.style.bgTint = false;
                S.style.bg = 'none';
                if (!S.style.frameTouched) { S.style.frame = 'none'; buildFrameThumbs(); }
                /* ⚠️ A picture wants the panel ON, and «unless one exists»
                   is not the same as «unless the author said otherwise». Having
                   looked at a vector background first leaves behind a scrim
                   record with `on: false` — a default nobody chose — and that
                   silently outranked the better default for a photograph. The
                   test is whether the CHECKBOX was touched, the same rule the
                   frame and the batch header already use. */
                if (!S.style.scrimTouched) {
                    S.style.scrim = { on: true, alpha: 0.78, pad: 7,
                        radius: (S.style.scrim && S.style.scrim.radius) || 6 };
                }
                return bakeBg().then(function () {
                    syncBgUi(); buildBgThumbs(); commitAfter('background_image');
                    /* A crest gives its colours; so does a background, and
                       matching the type to the picture is the difference between
                       a design and a collage. */
                    var img = new Image();
                    img.onload = function () { if (window.CertSmart) offerPalette(img); };
                    img.src = bgRawSrc;
                });
            }).catch(function () { toast(t('err_img'), 'error'); });
        };
        fr.onerror = function () { toast(t('err_img'), 'error'); };
        fr.readAsDataURL(file);
    }

    /**
     * Re-apply the adjustments to the ORIGINAL. Always from the original, never
     * from the last bake: blur on blur cannot be undone, and a slider that only
     * goes one way is a slider people stop touching.
     */
    function bakeBg() {
        if (!bgRawSrc || !window.CertBgImage) return Promise.resolve();
        var seq = ++bgBakeSeq;
        var pal = window.CertTemplates.palette(presetOf().pal) || {};
        return window.CertBgImage.bake(bgRawSrc, {
            gray: S.style.bgGray || 0,
            blur: S.style.bgBlur || 0,
            tint: S.style.bgTint ? (S.style.accent || pal.accent) : null
        }).then(function (out) {
            /* A slider dragged fast starts several bakes; only the last one is
               the picture the person is looking at. */
            if (seq !== bgBakeSeq) return;
            bgBaked = out.src;
            S.style.bgImageAR = out.ar;
            /* Measured on the BAKED picture — the preflight must judge what is
               going to be printed, not what was uploaded. */
            S.style.bgImageLuma = out.luma;
            schedule();
        }).catch(function () { /* keep the previous bake rather than blank the sheet */ });
    }

    /** Slider moves arrive faster than a 2400 px re-encode; the last one wins. */
    function bakeSoon() {
        clearTimeout(bgBakeTimer);
        bgBakeTimer = setTimeout(bakeBg, 160);
    }

    /** Show only the controls that have something to act on. */
    function syncBgUi() {
        var hasImg = !!bgRawSrc;
        var hasArt = hasImg || (S.style.bg && S.style.bg !== 'none');
        var el;
        if ((el = $('cm-bgImgOpts'))) el.hidden = !hasImg;
        if ((el = $('cm-bgClear'))) el.hidden = !hasImg;
        if ((el = $('cm-scrimBox'))) el.hidden = !hasArt;
        if (hasArt && !S.style.scrim) S.style.scrim = { on: false, alpha: 0.72, pad: 7 };
        var sc = S.style.scrim || {};
        if ((el = $('cm-scrim'))) el.checked = !!sc.on;
        if ((el = $('cm-scrimOpts'))) el.hidden = !sc.on;
        if ((el = $('cm-scrimAlpha'))) el.value = Math.round((sc.alpha == null ? 0.72 : sc.alpha) * 100);
        if ((el = $('cm-scrimPad'))) el.value = sc.pad == null ? 7 : sc.pad;
        if ((el = $('cm-bgOp'))) el.value = Math.round((S.style.bgImageOpacity == null ? 1 : S.style.bgImageOpacity) * 100);
        if ((el = $('cm-bgInset'))) el.value = S.style.bgInset == null ? 15 : S.style.bgInset;
        if ((el = $('cm-bgGray'))) el.value = Math.round((S.style.bgGray || 0) * 100);
        if ((el = $('cm-bgBlur'))) el.value = Math.round((S.style.bgBlur || 0) * 100);
        if ((el = $('cm-bgTint'))) el.checked = !!S.style.bgTint;
        if ((el = $('cm-scrimRadius'))) el.value = sc.radius == null ? 6 : sc.radius;
        /* The intensity dial belongs to the BUILT art, and only exists while
           there is some: on a photograph it would do nothing, and a control that
           does nothing teaches people to ignore the panel. */
        if ((el = $('cm-bgArtOpts'))) el.hidden = hasImg || !(S.style.bg && S.style.bg !== 'none');
        if ((el = $('cm-bgStrength'))) el.value = Math.round((S.style.bgStrength == null ? 1 : S.style.bgStrength) * 100);
    }

    function wireBackground() {
        $('cm-bgFile').addEventListener('change', function () { readBgImage(this.files && this.files[0]); });
        $('cm-bgClear').addEventListener('click', function () { clearBgImage(false); });
        $('cm-bgOp').addEventListener('input', function () {
            S.style.bgImageOpacity = (+this.value) / 100; schedule();
        });
        $('cm-bgOp').addEventListener('change', function () { commitAfter('bg_opacity'); });
        $('cm-bgInset').addEventListener('change', function () {
            S.style.bgInset = Math.max(5, Math.min(60, +this.value || 15)); commitAfter('bg_margin');
        });
        $('cm-scrim').addEventListener('change', function () {
            S.style.scrim = S.style.scrim || { alpha: 0.72, pad: 7 };
            S.style.scrim.on = this.checked;
            S.style.scrimTouched = true;
            syncBgUi(); commitAfter('scrim');
        });
        $('cm-scrimAlpha').addEventListener('input', function () {
            S.style.scrim = S.style.scrim || { on: true, pad: 7 };
            S.style.scrim.alpha = (+this.value) / 100; schedule();
        });
        $('cm-scrimAlpha').addEventListener('change', function () { commitAfter('scrim_alpha'); });
        $('cm-scrimPad').addEventListener('change', function () {
            S.style.scrim = S.style.scrim || { on: true, alpha: 0.72 };
            S.style.scrim.pad = Math.max(0, Math.min(24, +this.value || 7));
            commitAfter('scrim_pad');
        });
        $('cm-scrimRadius').addEventListener('change', function () {
            S.style.scrim = S.style.scrim || { on: true, alpha: 0.72, pad: 7 };
            S.style.scrim.radius = Math.max(0, Math.min(30, +this.value || 0));
            commitAfter('scrim_radius');
        });

        /* ── the picture's own three dials ─────────────────────────────────── */
        $('cm-bgGray').addEventListener('input', function () {
            S.style.bgGray = (+this.value) / 100; bakeSoon();
        });
        $('cm-bgGray').addEventListener('change', function () { bakeBg().then(function () { commitAfter('bg_gray'); }); });
        $('cm-bgBlur').addEventListener('input', function () {
            S.style.bgBlur = (+this.value) / 100; bakeSoon();
        });
        $('cm-bgBlur').addEventListener('change', function () { bakeBg().then(function () { commitAfter('bg_blur'); }); });
        $('cm-bgTint').addEventListener('change', function () {
            S.style.bgTint = this.checked;
            bakeBg().then(function () { commitAfter('bg_tint'); });
        });
        $('cm-bgStrength').addEventListener('input', function () {
            S.style.bgStrength = (+this.value) / 100; schedule();
        });
        $('cm-bgStrength').addEventListener('change', function () { commitAfter('bg_art_strength'); });

        /* One button, because the three dials are a means and «make this
           readable» is the end. The numbers come from the measured picture, not
           from a preference — see CertBgImage.suggest(). */
        $('cm-bgAuto').addEventListener('click', function () {
            if (!bgRawSrc) return;
            var pal = window.CertTemplates.palette(presetOf().pal) || {};
            var ink = S.style.ink || pal.ink || '#1d2b45';
            var lum = (function (h) {
                h = String(h).replace('#', '');
                if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
                return 0.2126 * parseInt(h.substr(0, 2), 16) + 0.7152 * parseInt(h.substr(2, 2), 16)
                    + 0.0722 * parseInt(h.substr(4, 2), 16);
            })(ink);
            var paperHex = S.style.paper || pal.paper || '#ffffff';
            var paperLum = paperHex === 'none' ? 255 : (function (h) {
                h = String(h).replace('#', '');
                if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
                return 0.2126 * parseInt(h.substr(0, 2), 16) + 0.7152 * parseInt(h.substr(2, 2), 16)
                    + 0.0722 * parseInt(h.substr(4, 2), 16);
            })(paperHex);
            var s2 = window.CertBgImage.suggest(S.style.bgImageLuma, lum < 128, paperLum);
            S.style.bgGray = s2.gray; S.style.bgBlur = s2.blur;
            S.style.bgImageOpacity = Math.max(0.1, Math.min(1, 1 - s2.fade));
            S.style.scrim = S.style.scrim || { pad: 7, radius: 6 };
            /* Where fading cannot reach the target — white type on a white
               photograph, or a picture too uneven for any single opacity — the
               panel is the remedy, and the button turns it on rather than
               moving a slider that will not help. */
            if (s2.scrim > 0) { S.style.scrim.on = true; S.style.scrim.alpha = s2.scrim; }
            if (S.style.scrim.alpha == null) S.style.scrim.alpha = 0.72;
            syncBgUi();
            bakeBg().then(function () {
                commitAfter('bg_auto');
                var note = $('cm-bgNote');
                if (note) {
                    note.hidden = false;
                    note.textContent = (UI.bg_auto_done || '')
                        .replace(':gray', Math.round(s2.gray * 100))
                        .replace(':blur', Math.round(s2.blur * 100))
                        .replace(':fade', Math.round((1 - s2.fade) * 100));
                }
            });
        });
    }

    function buildFrameThumbs() {
        var box = $('cm-frames');
        box.innerHTML = '';
        var cur = S.style.frame || presetOf().frame;
        var pal = window.CertTemplates.palette(presetOf().pal) || {};
        var ink = S.style.ink || pal.ink, accent = S.style.accent || pal.accent;
        window.CertFrames.list().forEach(function (id) {
            var b = document.createElement('button');
            b.type = 'button'; b.className = 'cm-thumb';
            b.setAttribute('aria-pressed', id === cur ? 'true' : 'false');
            b.appendChild(frameThumbSvg(id, ink, accent, 120));
            var s = document.createElement('span');
            s.textContent = (CFG.frames || {})[id] || id;
            b.appendChild(s);
            b.addEventListener('click', function () {
                S.style.frame = id; S.style.frameTouched = true; buildFrameThumbs(); commitAfter('frame');
            });
            box.appendChild(b);
        });
    }

    function buildPalettes() {
        var box = $('cm-palettes');
        box.innerHTML = '';
        var cur = S.style.pal || presetOf().pal;
        window.CertTemplates.PALETTES.forEach(function (p) {
            var b = document.createElement('button');
            b.type = 'button'; b.className = 'cm-sw';
            b.title = (CFG.palettes || {})[p.id] || p.id;
            b.setAttribute('aria-label', b.title);
            b.setAttribute('aria-pressed', p.id === cur ? 'true' : 'false');
            b.innerHTML = '<i style="background:' + p.ink + '"></i><i style="background:' + p.accent + '"></i>'
                + '<i style="background:' + (p.paper === 'none' ? '#ffffff' : p.paper) + '"></i>';
            b.addEventListener('click', function () { applyPalette(p); });
            box.appendChild(b);
        });
    }

    function applyPalette(p) {
        S.style.pal = p.id; S.style.ink = p.ink; S.style.accent = p.accent; S.style.paper = p.paper;
        $('cm-ink').value = p.ink; $('cm-accent').value = p.accent;
        $('cm-paperNone').checked = p.paper === 'none';
        if (p.paper !== 'none') $('cm-paper').value = p.paper;
        buildPalettes(); buildFrameThumbs(); buildBgThumbs(); commitAfter('palette');
    }

    function buildFontSelects() {
        $$('[data-fontsel]').forEach(function (sel) {
            sel.innerHTML = '';
            window.CertTemplates.FONTS.forEach(function (f) {
                var o = document.createElement('option');
                o.value = f.id; o.textContent = f.css.replace(/^CM /, '');
                o.style.fontFamily = cssFor(f.id);
                sel.appendChild(o);
            });
        });
        syncFontSelects();
    }
    function syncFontSelects() {
        var p = presetOf();
        $('cm-fTitle').value = S.style.font_title || p.title;
        $('cm-fName').value = S.style.font_name || p.name;
        $('cm-fBody').value = S.style.font_body || p.body;
    }

    function buildWordings() {
        var cats = CFG.wordings || {};
        var catBox = $('cm-wordCats'), itemBox = $('cm-wordItems');
        catBox.innerHTML = ''; itemBox.innerHTML = '';
        var keys = Object.keys(cats);
        if (!keys.length) return;
        var active = keys[0];
        function paintItems() {
            itemBox.innerHTML = '';
            ((cats[active] || {}).items || []).forEach(function (txt) {
                var b = document.createElement('button');
                b.type = 'button'; b.className = 'cm-chip wide';
                b.textContent = txt;
                b.addEventListener('click', function () {
                    S.texts.reason = txt; syncTextInputs(); commitAfter('wording');
                });
                itemBox.appendChild(b);
            });
        }
        keys.forEach(function (k) {
            var b = document.createElement('button');
            b.type = 'button'; b.className = 'cm-chip';
            b.textContent = (cats[k] || {}).label || k;
            b.setAttribute('aria-pressed', k === active ? 'true' : 'false');
            b.addEventListener('click', function () {
                active = k;
                $$('button', catBox).forEach(function (x) { x.setAttribute('aria-pressed', 'false'); });
                b.setAttribute('aria-pressed', 'true');
                paintItems();
            });
            catBox.appendChild(b);
        });
        paintItems();
    }

    function applyPreset(id, keepTexts) {
        var p = window.CertTemplates.preset(id);
        if (!p) return;
        S.preset = id; S.type = p.type; S.page = null;
        /* A preset is a starting point, so choosing one clears the overrides it
         * is supposed to define — otherwise the picker looks broken: you pick
         * "Спортивная" and keep the previous frame. */
        S.style = {}; S.nudge = {}; S.blockStyle = {};
        S.overprint = id === 'overprint';
        if (!keepTexts || !Object.keys(S.texts).length) {
            var sample = (CFG.samples || {})[p.type] || {};
            S.texts = {};
            Object.keys(sample).forEach(function (k) { S.texts[k] = sample[k]; });
            S.texts.date = dateNow($('cm-dateStyle') ? $('cm-dateStyle').value : 'quoted');
        }
        syncAll();
        commitAfter('preset');
        fun('preset', { preset: id });
    }

    function dateNow(style) {
        if (style === 'none') return '';
        return window.CertData.formatDate(new Date(), style || 'quoted', CFG.months || [], CFG.dateFormats || {});
    }

    function syncTextInputs() {
        $$('[data-txt]').forEach(function (el) {
            var v = S.texts[el.getAttribute('data-txt')] || '';
            if (el.value !== v) el.value = v;
        });
        /* The selection pane shows the same text a third time. Rebuilding it
           while its own box has focus would eat the caret, so it is refreshed
           only when the change came from somewhere else — a paste, an undo, or
           an edit made on the sheet. */
        var pane = $('cm-selBody');
        if (editor && editor.selected() && pane && !pane.contains(document.activeElement)) {
            paintSelection(editor.selected());
        }
    }

    function syncAll() {
        syncTextInputs();
        var p = presetOf();
        var pal = window.CertTemplates.palette(p.pal) || {};
        $('cm-type').value = S.type;
        $('cm-page').value = S.page || p.page;
        $('cm-ink').value = S.style.ink || pal.ink;
        $('cm-accent').value = S.style.accent || pal.accent;
        var paper = S.style.paper || pal.paper;
        $('cm-paperNone').checked = paper === 'none';
        if (paper !== 'none') $('cm-paper').value = paper;
        $('cm-wm').checked = S.style.wm == null ? !!p.wm : !!S.style.wm;
        $('cm-wmSize').value = S.style.wmSize == null ? 30 : S.style.wmSize;
        $('cm-wmOp').value = S.style.wmOp == null ? 15 : S.style.wmOp;
        $('cm-valign').value = S.style.vAlign || 'center';
        $('cm-pad').value = S.style.pad == null ? 6 : S.style.pad;
        $('cm-overprint').checked = S.overprint;
        $('cm-overprintBox').hidden = !S.overprint;
        $('cm-blankForm').checked = S.blankForm;
        $('cm-batchOn').checked = S.batch.on;
        $('cm-batchBox').hidden = !(S.batch.raw || '').trim();
        $('cm-zip').hidden = !S.batch.on;
        $('cm-list').value = S.batch.raw || '';
        $('cm-case').value = S.batch.kase;
        $('cm-fixCaps').checked = !!S.batch.fixCaps;
        paintCaseExample();
        exportLabel();
        buildPresetThumbs(); buildFrameThumbs(); buildBgThumbs(); buildPalettes(); syncFontSelects(); paintOverlays();
        syncBgUi();
    }

    function exportLabel() {
        var lab = S.batch.on ? (UI.export_batch || UI.export_pdf) : UI.export_pdf;
        $$('#cm-pdf .cm-btn-label').forEach(function (e) { e.textContent = lab; });
        var b2 = $('cm-pdf2');
        if (b2) b2.lastChild.textContent = lab;
    }

    /* ── overlays ────────────────────────────────────────────────────────── */

    var OV_DEFAULT = {
        logo:      { x: 0.13, y: 0.15, w: 22 },
        signature: { x: 0.72, y: 0.79, w: 40, keyWhite: true },
        stamp:     { x: 0.33, y: 0.77, w: 34, keyWhite: true, opacity: 0.92 }
    };

    function addOverlay(kind) {
        if (kind === 'medallion') {
            S.overlays.push({ id: 'ov' + (++ovSeq), kind: 'medallion', x: 0.86, y: 0.21, w: 22 });
            paintOverlays(); commitAfter('overlay_add'); return;
        }
        if (kind === 'qr') {
            S.overlays.push({ id: 'ov' + (++ovSeq), kind: 'qr', x: 0.93, y: 0.9, w: 18, value: '' });
            paintOverlays(); commitAfter('overlay_add'); return;
        }
        pendingOv = kind;
        $('cm-ovFile').click();
    }

    /* White → alpha, done ONCE at upload. A blend mode would fix the preview and
     * the PNG and leave the PDF with a white box, because pdf-lib cannot blend:
     * three renderers can only agree if there is nothing left to disagree about. */
    function keyOutWhite(src) {
        return new Promise(function (resolve) {
            var img = new Image();
            img.onload = function () {
                try {
                    var cv = document.createElement('canvas');
                    cv.width = img.naturalWidth; cv.height = img.naturalHeight;
                    var c = cv.getContext('2d');
                    c.drawImage(img, 0, 0);
                    var d = c.getImageData(0, 0, cv.width, cv.height);
                    var px = d.data, cut = 238, soft = 200;
                    for (var i = 0; i < px.length; i += 4) {
                        var m = Math.min(px[i], px[i + 1], px[i + 2]);
                        if (m >= cut) px[i + 3] = 0;
                        else if (m > soft) px[i + 3] = Math.round(px[i + 3] * (cut - m) / (cut - soft));
                    }
                    c.putImageData(d, 0, 0);
                    resolve(cv.toDataURL('image/png'));
                } catch (e) { resolve(src); }
            };
            img.onerror = function () { resolve(src); };
            img.src = src;
        });
    }

    function readOverlayImage(file) {
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) { toast(t('err_big'), 'error'); return; }
        var fr = new FileReader();
        fr.onload = function () {
            var d = OV_DEFAULT[pendingOv] || OV_DEFAULT.logo;
            var ov = { id: 'ov' + (++ovSeq), kind: 'image', role: pendingOv, src: fr.result, raw: fr.result };
            for (var k in d) ov[k] = d[k];
            if (ov.keyWhite) keyOutWhite(fr.result).then(function (out) { ov.src = out; schedule(); });
            var img = new Image();
            img.onload = function () {
                ov.h = ov.w * (img.naturalHeight / img.naturalWidth);
                paintOverlays(); schedule();
                /* A crest carries the organisation's colours; offering them is
                 * cheaper for everyone than asking for a hex code. */
                if (pendingOv === 'logo' && window.CertSmart) offerPalette(img);
            };
            img.onerror = function () { toast(t('err_img'), 'error'); };
            img.src = fr.result;
            S.overlays.push(ov);
            paintOverlays(); commitAfter('overlay_add');
        };
        fr.onerror = function () { toast(t('err_img'), 'error'); };
        fr.readAsDataURL(file);
    }

    function offerPalette(img) {
        var pal = window.CertSmart.paletteFrom(img);
        if (!pal) return;
        var box = $('cm-overlays');
        var note = document.createElement('div');
        note.className = 'cm-note cm-note-info';
        note.innerHTML = '<div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">'
            + '<span style="display:inline-flex;border-radius:5px;overflow:hidden;border:1px solid var(--border)">'
            + '<i style="width:18px;height:18px;display:block;background:' + pal.ink + '"></i>'
            + '<i style="width:18px;height:18px;display:block;background:' + pal.accent + '"></i>'
            + '<i style="width:18px;height:18px;display:block;background:' + pal.paper + '"></i></span>'
            + '<span style="flex:1 1 120px">' + esc(t('palette_offer')) + '</span></div>';
        var yes = document.createElement('button');
        yes.type = 'button'; yes.className = 'cm-btn'; yes.style.marginTop = '7px';
        yes.textContent = t('palette_apply');
        yes.addEventListener('click', function () {
            applyPalette({ id: 'custom', ink: pal.ink, accent: pal.accent, paper: pal.paper });
            note.remove();
            fun('palette_from_logo', {});
        });
        note.appendChild(yes);
        box.appendChild(note);
    }

    function paintOverlays() {
        var box = $('cm-overlays');
        box.innerHTML = '';
        if (!S.overlays.length) {
            box.innerHTML = '<p class="cm-hint">' + esc(t('no_marks')) + '</p>';
            return;
        }
        S.overlays.forEach(function (ov) {
            var row = document.createElement('div');
            row.className = 'cm-imgrow';
            var label = ov.kind === 'medallion' ? UI.medallion : ov.kind === 'qr' ? UI.qr : (UI[ov.role] || UI.logo);
            row.innerHTML = (ov.src ? '<img src="' + ov.src + '" alt="">'
                : '<span style="width:30px;text-align:center">' + (ov.kind === 'qr' ? '▦' : '✿') + '</span>')
                + '<span class="nm">' + esc(label) + '</span>';

            var pick = document.createElement('button');
            pick.type = 'button'; pick.className = 'cm-btn ghost'; pick.textContent = t('select_on_sheet');
            pick.style.fontSize = '11.5px'; pick.style.height = '26px';
            pick.addEventListener('click', function () { editor.select(ov.id, 'panel'); });
            row.appendChild(pick);

            var del = document.createElement('button');
            del.type = 'button'; del.className = 'cm-link'; del.textContent = '×';
            del.setAttribute('aria-label', UI.remove);
            del.style.fontSize = '17px';
            del.addEventListener('click', function () {
                S.overlays = S.overlays.filter(function (o) { return o !== ov; });
                if (editor.selected() === ov.id) editor.select(null);
                paintOverlays(); commitAfter('overlay_remove');
            });
            row.appendChild(del);
            box.appendChild(row);

            if (ov.kind === 'image') {
                var lab = document.createElement('label');
                lab.className = 'cm-inline';
                var cb = document.createElement('input');
                cb.type = 'checkbox'; cb.className = 'cm-check'; cb.checked = !!ov.keyWhite;
                var sp = document.createElement('span');
                sp.textContent = UI.multiply || '';
                lab.appendChild(cb); lab.appendChild(sp);
                cb.addEventListener('change', function () {
                    ov.keyWhite = cb.checked;
                    if (!cb.checked) { ov.src = ov.raw || ov.src; commitAfter('keywhite'); return; }
                    keyOutWhite(ov.raw || ov.src).then(function (out) { ov.src = out; commitAfter('keywhite'); });
                });
                box.appendChild(lab);
            }
            if (ov.kind === 'qr') {
                var f = document.createElement('input');
                f.type = 'text'; f.className = 'cm-input'; f.placeholder = UI.qr_value || '';
                f.value = ov.value || ''; f.style.marginBottom = '7px';
                f.addEventListener('input', function () {
                    ov.value = f.value;
                    ov.matrix = f.value ? qrMatrix(f.value) : null;
                    schedule();
                });
                f.addEventListener('change', function () { editor.commit('qr'); });
                box.appendChild(f);
                var h = document.createElement('p');
                h.className = 'cm-hint'; h.textContent = UI.qr_hint || '';
                box.appendChild(h);
            }
        });
    }

    var _qr = null;
    function qrMatrix(value) {
        if (!window.qrcode) {
            if (!_qr) {
                _qr = new Promise(function (res, rej) {
                    var s = document.createElement('script');
                    s.src = '/vendor/qrcode/qrcode.min.js';
                    s.onload = res; s.onerror = rej;
                    document.head.appendChild(s);
                });
                _qr.then(function () {
                    S.overlays.forEach(function (o) { if (o.kind === 'qr' && o.value) o.matrix = qrMatrix(o.value); });
                    schedule();
                }).catch(function () {});
            }
            return null;
        }
        try {
            var q = window.qrcode(0, 'M');
            q.addData(value); q.make();
            var n = q.getModuleCount(), m = [];
            for (var r = 0; r < n; r++) {
                m[r] = [];
                for (var c = 0; c < n; c++) m[r][c] = q.isDark(r, c) ? 1 : 0;
            }
            return m;
        } catch (e) { return null; }
    }

    /* ── batch ───────────────────────────────────────────────────────────── */

    function reparse() {
        var raw = S.batch.raw || '';
        /* Forced only when a HUMAN ticked the box. Left as an inference the
           parser sniffs each list on its own merits, which is what a user who
           imports a CSV and then pastes a plain list expects. */
        lastRows = raw.trim()
            ? window.CertData.parseList(raw, {
                header: S.batch.headerTouched ? !!$('cm-hasHeader').checked : undefined
            })
            : { rows: [], headers: null };
        $('cm-hasHeader').checked = !!lastRows.headers;
        S.batch.index = Math.min(S.batch.index, Math.max(0, lastRows.rows.length - 1));
        /* The list IS the switch. Rows present → batch on, unless the user has
           deliberately unticked «print all» or is filling a blank form (where a
           batch prints N identical empty sheets against N units of the cap). */
        var n = lastRows.rows.length;
        $('cm-batchBox').hidden = !n;
        if (n) bindNameToList();
        if (!n) { S.batch.on = false; $('cm-batchOn').checked = false; }
        else if (!S.batch.userOff && !S.blankForm) { S.batch.on = true; $('cm-batchOn').checked = true; }
        $('cm-batchOnLabel').textContent = n ? (UI.batch_on + ' — ' + n) : UI.batch_on;
        /* An option for a situation you are not in is noise. */
        $('cm-capsRow').hidden = !listHasCaps();
        $('cm-registry').hidden = !(S.batch.on && n);
        $('cm-zip').hidden = !S.batch.on;
        paintCols(); paintRows(); exportLabel();
    }

    /** Points the recipient field at the list the first time a list appears.
     *
     * Without this the batch is a feature you must READ ABOUT to use: the field
     * holds literal sample text, `fill()` finds no token, and all N sheets carry
     * the same name. Done once (`S.batch.bound`) so a user who deliberately
     * writes a fixed name over the token is not overruled on the next keystroke,
     * and it is a normal history step, so ⌘Z puts the old text back. */
    function bindNameToList() {
        if (S.batch.bound) return;
        var cur = (S.texts.name || '').trim();
        if (/\{[^}]+\}/.test(cur)) { S.batch.bound = 1; return; }   // already a token
        S.batch.bound = 1;
        S.texts.name = '{фио}';
        var el = document.querySelector('[data-txt="name"]');
        if (el) el.value = S.texts.name;
        toast(t('name_bound'), 'info');
        fun('name_bound', {});
        if (editor) editor.commit('bind_name');
    }

    function paintCaseExample() {
        var sel = $('cm-case'), o = sel.options[sel.selectedIndex];
        $('cm-caseEx').textContent = (o && o.getAttribute('data-ex')) || '';
    }

    function paintCols() {
        var wrap = $('cm-colsWrap'), box = $('cm-cols');
        var hs = lastRows && lastRows.headers;
        var cols = hs || (lastRows && lastRows.rows[0] ? lastRows.rows[0].map(function (_, i) { return String(i + 1); }) : []);
        wrap.hidden = cols.length < 2;
        box.innerHTML = '';
        var fio = document.createElement('button');
        fio.type = 'button'; fio.className = 'cm-chip'; fio.textContent = '{фио}';
        fio.addEventListener('click', function () { insertToken('{фио}'); });
        box.appendChild(fio);
        /* The gender fork only means anything where we can READ the gender, and
           that is the Russian name engine. Offering it in ko or pt would insert
           a token that always picks the first word — a promise we cannot keep. */
        if (CFG.locale === 'ru') {
            var gf = document.createElement('button');
            gf.type = 'button'; gf.className = 'cm-chip'; gf.textContent = '{ученик/ученица}';
            gf.title = UI.fork_hint || '';
            gf.addEventListener('click', function () { insertToken('{ученик/ученица}'); });
            box.appendChild(gf);
        }
        cols.forEach(function (c) {
            var b = document.createElement('button');
            b.type = 'button'; b.className = 'cm-chip'; b.textContent = '{' + c + '}';
            b.addEventListener('click', function () { insertToken('{' + c + '}'); });
            box.appendChild(b);
        });
        /* Offered only when the list has real column NAMES — «1, 2, 3» carries
           no meaning to match against. */
        $('cm-bindCols').hidden = !(hs && hs.length > 1);
    }

    /* Which document field a spreadsheet column most likely belongs in.
     *
     * Deliberately a short, boring table rather than anything clever: a wrong
     * guess writes a wrong word onto a certificate, so only headings whose
     * meaning is unambiguous are here, and a column that matches nothing is
     * simply left for the user to place by hand. Latin and Cyrillic together
     * because Russian registries are exported with either. */
    var COLUMN_FIELDS = [
        ['detail', ['класс', 'группа', 'должность', 'отдел', 'кружок', 'секция', 'курс', 'команда',
                    'class', 'grade', 'group', 'role', 'position', 'department', 'team', 'course']],
        ['reason', ['награда', 'за что', 'заслуга', 'достижение', 'номинация', 'основание', 'причина',
                    'reason', 'award', 'achievement', 'nomination', 'for', 'merit']],
        ['place',  ['город', 'место', 'населённый пункт', 'city', 'town', 'place', 'location']],
        ['date',   ['дата', 'date']],
        ['org',    ['организация', 'школа', 'учреждение', 'компания', 'org', 'organisation',
                    'organization', 'school', 'company']],
        ['number', ['номер', '№', 'number', 'no', 'id']],
        ['subtitle', ['степень', 'место в конкурсе', 'degree', 'rank']],
    ];

    /** Puts the recognised columns into the text, once, without ever
     *  overwriting something the user wrote.
     *
     * The «is it still the sample?» test is what makes this safe: a field the
     * author has touched no longer equals the preset's sample string, so it is
     * skipped. Suggesting is cheap; clobbering a typed line is not. */
    function bindColumns() {
        var hs = lastRows && lastRows.headers;
        if (!hs || !hs.length) return 0;
        var sample = (CFG.samples || {})[S.type] || {};
        var used = {}, n = 0;
        hs.forEach(function (h) {
            var key = String(h || '').trim().toLocaleLowerCase('ru');
            if (!key) return;
            for (var i = 0; i < COLUMN_FIELDS.length; i++) {
                var field = COLUMN_FIELDS[i][0], words = COLUMN_FIELDS[i][1];
                if (used[field]) continue;
                var hit = words.some(function (w) { return key === w || key.indexOf(w) === 0; });
                if (!hit) continue;
                var cur = String(S.texts[field] == null ? '' : S.texts[field]);
                var untouched = cur === '' || cur === String(sample[field] == null ? '' : sample[field]);
                if (!untouched) { used[field] = 1; return; }
                S.texts[field] = '{' + h + '}';
                used[field] = 1; n++;
                return;
            }
        });
        if (!n) return 0;
        syncTextInputs();
        commitAfter('bind_cols');
        return n;
    }

    var lastFocused = null;
    function insertToken(tok) {
        var el = lastFocused || document.querySelector('[data-txt="name"]');
        if (!el) return;
        var s = el.selectionStart == null ? el.value.length : el.selectionStart;
        var e = el.selectionEnd == null ? s : el.selectionEnd;
        el.value = el.value.slice(0, s) + tok + el.value.slice(e);
        el.focus();
        el.setSelectionRange(s + tok.length, s + tok.length);
        S.texts[el.getAttribute('data-txt')] = el.value;
        commitAfter('token');
    }

    function paintRows() {
        var box = $('cm-rows');
        if (!lastRows || !lastRows.rows.length) {
            box.innerHTML = '<table><tr><td>' + esc(UI.batch_empty || '') + '</td></tr></table>';
            paintBatchNote(0);
            return;
        }
        var html = ['<table>'];
        lastRows.rows.forEach(function (r, i) {
            var name = window.CertData.rowContext(r, lastRows.headers, i).fio || '';
            var flags = [];
            if (name.trim().split(/\s+/).length < 2) flags.push(t('name_short'));
            var v = window.CertData.nameVerdict(name);
            if (v && (v.ambiguous || v.unknown)) flags.push(t('name_ambig'));
            /* A guessed gender only MATTERS when some word depends on it. */
            if (usesFork() && (!v || v.ambiguous || v.unknown)) flags.push(t('fork_guessed'));
            var res = buildOne(i);
            if (res.warnings.some(function (w) { return w.code === 'overflow'; })) flags.push(t('row_overflow'));
            html.push('<tr data-row="' + i + '"' + (i === S.batch.index ? ' data-cur="1"' : '') + '>'
                + '<td class="num">' + (i + 1) + '</td><td>' + esc(name) + '</td>'
                + '<td class="flag">' + esc(flags.join(' · ')) + '</td></tr>');
        });
        html.push('</table>');
        box.innerHTML = html.join('');
        $$('tr[data-row]', box).forEach(function (tr) {
            tr.addEventListener('click', function () {
                S.batch.index = +tr.getAttribute('data-row');
                paintRows(); schedule();
            });
        });
        paintBatchNote(lastRows.rows.length);
    }

    /** True when any text field carries a gender fork. */
    function usesFork() {
        for (var k in S.texts) {
            if (window.CertData.forks(S.texts[k], null).forks) return true;
        }
        return false;
    }

    function batchCap() {
        var tl = window.__tierLimits || {};
        var v = tl.certificate_max_people;
        /* 0 means CLOSED only for an unverified account — that flag is the one
           signal that distinguishes it from "unconfigured". Everywhere else a
           missing value fails OPEN, the rule every other client here uses. */
        if (v === 0 && tl.caps_blocked) return 0;
        return v > 0 ? v : 30;
    }

    function paintBatchNote(n) {
        var boxes = [$('cm-batchNote'), $('cm-batchNote2')].filter(Boolean);
        var cap = batchCap();
        var over = !!n && n > cap;
        var html = '';
        if (over) {
            var planEl = document.querySelector('[data-tool-limit]');
            var plan = (planEl && planEl.getAttribute('data-tl-plan-paid')) || '';
            html = '<div class="cm-note cm-note-warn" data-cm-wall>'
                + esc(t('batch_over', { n: n, cap: cap, plan: plan, paid: CFG.paidBatch || 1000 })) + '</div>';
        }
        boxes.forEach(function (b) { b.innerHTML = html; });
        /* aria-disabled, never `disabled`: the press must still reach guard(),
           because that is what lets the wall lift itself when the list shrinks,
           when Premium is bought in another tab, or at midnight. */
        ['cm-pdf', 'cm-pdf2', 'cm-png', 'cm-zip'].forEach(function (id) {
            var el = $(id);
            if (!el) return;
            el.classList.toggle('cm-btn-blocked', over);
            if (over) el.setAttribute('aria-disabled', 'true'); else el.removeAttribute('aria-disabled');
        });
    }

    function nudgeWall() {
        var el = ($('cm-batchNote2') && $('cm-batchNote2').querySelector('[data-cm-wall]'))
            || ($('cm-batchNote') && $('cm-batchNote').querySelector('[data-cm-wall]'));
        if (!el) return;
        el.classList.remove('cm-note-pulse');
        void el.offsetWidth;
        el.classList.add('cm-note-pulse');
        el.scrollIntoView({ block: 'nearest' });
        fun('batch_wall', { rows: lastRows ? lastRows.rows.length : 0, cap: batchCap() });
    }

    /* ── export ──────────────────────────────────────────────────────────── */

    /** The award register: the same rows, in the same order the PDF prints.
     *
     * Semicolons and a BOM because the audience opens this in Russian Excel,
     * where a comma-separated UTF-8 file lands in one column as mojibake. The
     * signature column is empty on purpose — it is signed on paper. */
    function exportRegistry() {
        if (!lastRows || !lastRows.rows.length) return;
        var head = [t('reg_head_no'), t('reg_head_name'), t('reg_head_reason'),
                    t('reg_head_date'), t('reg_head_sign')];
        var lines = [head];
        lastRows.rows.forEach(function (r, i) {
            var texts = textsFor(i);
            var ctx = window.CertData.rowContext(r, lastRows.headers, i);
            lines.push([
                S.batch.num ? seqAt(i) : String(i + 1),
                (S.batch.fixCaps ? decaps(ctx.fio || '') : (ctx.fio || '')),
                texts.reason || '', texts.date || '', ''
            ]);
        });
        var csv = lines.map(function (row) {
            return row.map(function (c) {
                c = String(c == null ? '' : c);
                return /[";\r\n]/.test(c) ? '"' + c.replace(/"/g, '""') + '"' : c;
            }).join(';');
        }).join('\r\n');
        dl(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }), baseName() + '.csv');
        fun('registry', { rows: lastRows.rows.length });
    }

    function outputCount() { return S.batch.on && lastRows && lastRows.rows.length ? lastRows.rows.length : 1; }

    /** One composite key per RECIPIENT SET — see config/tiers.php for why. */
    function limitKey() {
        var names = (S.batch.on && lastRows)
            ? lastRows.rows.map(function (r, i) { return window.CertData.rowContext(r, lastRows.headers, i).fio || ''; })
            : [S.texts.name || ''];
        return 'set:' + hash(names.slice().sort().join('|') + '|' + S.type);
    }
    function hash(s) {
        var h = 5381;
        for (var i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
        return h.toString(36);
    }

    function guard() {
        var n = outputCount(), cap = batchCap();
        if (S.batch.on && (!lastRows || !lastRows.rows.length)) { toast(t('no_rows'), 'warning'); return null; }
        if (S.batch.on && n > cap) { paintBatchNote(n); nudgeWall(); return null; }
        if (!window.__toolLimit) return { commit: function () {}, abort: function () {} };
        return window.__toolLimit.begin(SLUG, [limitKey()]);
    }

    function baseName() {
        var kind = (CFG.types || {})[S.type] || t('file_name');
        var n = outputCount(), stem;
        if (S.blankForm) stem = kind;
        else if (S.batch.on && n > 1) stem = kind + ' ' + n;
        else {
            var who = String(S.texts.name || '').replace(/\{[^}]*\}/g, '').trim();
            stem = who ? kind + ' ' + who : kind;
        }
        if (window.__outputName) return window.__outputName.sanitize(stem) || t('file_name');
        return stem.replace(/[\\/:*?"<>|]+/g, ' ').trim() || t('file_name');
    }

    function allPages() {
        var n = outputCount(), out = [];
        for (var i = 0; i < n; i++) out.push(buildOne(i));
        return out;
    }
    function outOpts() {
        return {
            bleed: +S.out.bleed || 0, crop: !!S.out.crop, rulerMm: S.out.ruler ? 50 : 0,
            scale: +S.out.scale || 0, dx: +S.out.dx || 0, dy: +S.out.dy || 0, title: baseName()
        };
    }

    function busyAll(on, label) {
        ['cm-pdf', 'cm-pdf2'].forEach(function (id) {
            var b = $(id);
            if (!b) return;
            b.disabled = !!on;
            var lab = b.querySelector('.cm-btn-label') || b.lastChild;
            if (lab && label) lab.textContent = label;
        });
    }

    function exportPdf() {
        var run = guard();
        if (!run) return;
        stage('export_pdf');
        busyAll(true, t('building'));
        var pages = allPages();
        window.CertPdf.make(pages, outOpts()).then(function (bytes) {
            dl(new Blob([bytes], { type: 'application/pdf' }), baseName() + '.pdf');
            run.commit();
            done('pdf', pages.length, bytes.length);
        }).catch(function (e) {
            run.abort(); err(e, 'export_pdf');
        }).then(function () { busyAll(false); exportLabel(); });
    }

    function exportPng() {
        var run = guard();
        if (!run) return;
        stage('export_png');
        var btn = $('cm-png');
        btn.disabled = true; btn.textContent = t('png_building');
        var res = buildOne(S.batch.on ? S.batch.index : 0);
        window.CertRender.toCanvas(res, +S.out.dpi || 300, cssFor).then(function (canvas) {
            return new Promise(function (resolve) { canvas.toBlob(resolve, 'image/png'); });
        }).then(function (blob) {
            if (!blob) throw new Error('png_failed');
            dl(blob, baseName() + '.png');
            run.commit();
            done('png', 1, blob.size);
        }).catch(function (e) {
            run.abort(); err(e, 'export_png');
        }).then(function () { btn.disabled = false; btn.textContent = UI.export_png; });
    }

    /* One PDF per recipient, zipped. The default stays the single multi-page
       file because fonts are embedded ONCE there.

       The archive is packed by __batchDeliver, which owns entry names: until
       26.09.2026 this function kept its own counter, which named the second
       «Иванов Иван» «Иванов Иван (2).pdf» without marking that name taken — a
       recipient literally called «Иванов Иван (2)» then REPLACED that entry
       (JSZip.file() overwrites in silence), and names differing only in case
       collided on unpacking under Windows and macOS. */
    function exportZip() {
        var run = guard();
        if (!run) return;
        stage('export_zip');
        var btn = $('cm-zip');
        btn.disabled = true; btn.textContent = t('zip_building');
        var n = outputCount(), items = [], chain = Promise.resolve();
        for (var i = 0; i < n; i++) {
            (function (idx) {
                chain = chain.then(function () {
                    btn.textContent = t('building_n', { i: idx + 1, n: n });
                    var ctx = lastRows ? window.CertData.rowContext(lastRows.rows[idx], lastRows.headers, idx) : {};
                    var who = ctx.fio || String(idx + 1);
                    var o = outOpts(); o.title = who;
                    return window.CertPdf.make([buildOne(idx)], o).then(function (bytes) {
                        items.push({ name: safeName(who) + '.pdf', blob: new Blob([bytes], { type: 'application/pdf' }) });
                    });
                });
            })(i);
        }
        chain.then(function () {
            return window.__batchDeliver ? window.__batchDeliver : window.__ensureBatchDeliver();
        }).then(function (BD) {
            return BD.packZip(items);
        }).then(function (blob) {
            dl(blob, baseName() + '.zip');
            run.commit();
            done('zip', items.length, blob.size);
        }).catch(function (e) {
            run.abort(); err(e, 'export_zip');
        }).then(function () { btn.disabled = false; btn.textContent = UI.export_zip; });
    }

    function safeName(s) {
        return window.__outputName ? (window.__outputName.sanitize(s) || 'doc')
            : String(s).replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'doc';
    }

    function dl(blob, name) {
        if (window.__downloadFile) window.__downloadFile(blob, name);
        else {
            var a = document.createElement('a');
            a.href = URL.createObjectURL(blob); a.download = name; a.click();
            setTimeout(function () { URL.revokeObjectURL(a.href); }, 4000);
        }
    }
    function toast(msg, kind) { if (window.__toast) window.__toast(msg, kind || 'info'); }
    function err(e, where) {
        toast(t('err_pdf'), 'error');
        /* An export that fails is the one event worth waking someone for: the
           user has already decided to pay us their attention and got nothing. */
        fun('export_fail', {
            where: where,
            name: (e && e.name) || 'Error',
            msg: String((e && e.message) || e).slice(0, 120),
            rows: lastRows ? lastRows.rows.length : 0,
        });
        if (window.__aeCritical) window.__aeCritical('export_fail', { where: where, name: (e && e.name) || 'Error' });
        if (window.__handleError) window.__handleError(e, SLUG + ':' + where);
    }
    function done(kind, pages, bytes) {
        toast(t('ready'), 'success');
        if (window.__aeToolComplete) {
            window.__aeToolComplete({
                out: kind, pages: pages, bytes: bytes, preset: S.preset,
                frame: S.style.frame || presetOf().frame,
                batch: S.batch.on ? 1 : 0, overprint: S.overprint ? 1 : 0, blank: S.blankForm ? 1 : 0,
                edited: Object.keys(S.nudge).length + Object.keys(S.blockStyle).length,
                /* The four differentiators. If these read zero for a month, the
                   tool is competing on the features everyone already has. */
                decline: (S.batch.on && S.batch.kase !== 'nomn') ? S.batch.kase : '',
                forks: usesFork() ? 1 : 0,
                marks: (S.overlays || []).length,
                page: S.page || '',
                calib: (S.out && (+S.out.kScale || +S.out.kDx || +S.out.kDy)) ? 1 : 0,
                ms: Date.now() - T0
            });
        }
    }
    function stage(name) { if (window.__stage) window.__stage(name); }
    function fun(name, data) { if (window.__aeFunnel) window.__aeFunnel(name, data || {}); }

    /* «Was this discovered?» is a yes/no question, so each kind reports once.
       A drag would otherwise send an event per pointermove and drown the rest. */
    var _seen = {};
    function funOnce(name, data) { if (!_seen[name]) { _seen[name] = 1; fun(name, data || {}); } }
    function stageOnce(name) { if (!_seen['s:' + name]) { _seen['s:' + name] = 1; stage(name); } }

    /* ── persistence ─────────────────────────────────────────────────────── */

    var SAVE_KEY = 'cm:project';
    function snapshot() {
        return {
            v: 2, type: S.type, preset: S.preset, page: S.page, texts: S.texts, style: S.style,
            blockStyle: S.blockStyle, nudge: S.nudge, overlays: S.overlays,
            batch: S.batch, out: S.out, overprint: S.overprint, blankForm: S.blankForm,
            /* The ORIGINAL, not the bake: a project stores what the author gave
               us plus what they asked for, and rebuilds the result on opening.
               Storing the bake would freeze the sliders into the file. */
            bgRaw: bgRawSrc || undefined
        };
    }
    function save() { try { localStorage.setItem(SAVE_KEY, JSON.stringify(snapshot())); } catch (e) {} }
    function restore() {
        var raw;
        try { raw = localStorage.getItem(SAVE_KEY); } catch (e) { return false; }
        if (!raw) return false;
        try { return adopt(JSON.parse(raw)); } catch (e) { return false; }
    }
    function adopt(o) {
        if (!o || !o.preset || !window.CertTemplates.preset(o.preset)) return false;
        S.type = o.type || S.type; S.preset = o.preset; S.page = o.page || null;
        S.texts = o.texts || {}; S.style = o.style || {}; S.nudge = o.nudge || {};
        S.blockStyle = o.blockStyle || {};
        S.overlays = o.overlays || []; S.overprint = !!o.overprint; S.blankForm = !!o.blankForm;
        if (o.batch) for (var k in o.batch) S.batch[k] = o.batch[k];
        if (o.out) for (var j in o.out) S.out[j] = o.out[j];
        ovSeq = S.overlays.length;
        bgRawSrc = o.bgRaw || null;
        bgBaked = null;
        /* v2 files written before the adjustments existed kept the picture in
           the style. Take it as the original rather than dropping it. */
        if (!bgRawSrc && S.style && S.style.bgImage) { bgRawSrc = S.style.bgImage; }
        delete S.style.bgImage;
        if (bgRawSrc) bakeBg();
        return true;
    }

    /* ── wiring ──────────────────────────────────────────────────────────── */

    function wireTabs() {
        var tabs = $$('.cm-tab');
        tabs.forEach(function (b, i) {
            b.addEventListener('click', function () {
                tab = b.getAttribute('data-tab');
                tabs.forEach(function (x) { x.setAttribute('aria-selected', x === b ? 'true' : 'false'); });
                $$('.cm-pane').forEach(function (p) { p.hidden = p.getAttribute('data-pane') !== tab; });
                fun('tab', { tab: tab });
            });
            /* A `role=tablist` that only answers to Tab-then-Enter is a tablist
               in name only: arrows are how anyone driving it from the keyboard
               expects to move between tabs. Home/End for the same reason. */
            b.addEventListener('keydown', function (e) {
                /* Arrows follow the reading direction, so in Arabic the right
                   arrow still means "the next tab over there". */
                var rtl = (document.documentElement.dir || '').toLowerCase() === 'rtl' ? -1 : 1;
                var d = e.key === 'ArrowRight' ? rtl : e.key === 'ArrowLeft' ? -rtl : 0;
                var to = e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1
                    : d ? (i + d + tabs.length) % tabs.length : -1;
                if (to < 0) return;
                e.preventDefault();
                tabs[to].focus();
                tabs[to].click();
            });
        });
    }

    function wireHead() {
        $('cm-type').addEventListener('change', function () {
            S.type = this.value;
            var first = window.CertTemplates.PRESETS.filter(function (p) { return p.type === S.type; })[0];
            if (first) applyPreset(first.id, false);
        });
        $('cm-page').addEventListener('change', function () { S.page = this.value; commitAfter('page'); });
        $('cm-undo').addEventListener('click', function () { editor.undo(); });
        $('cm-redo').addEventListener('click', function () { editor.redo(); });
        $('cm-shuffle').addEventListener('click', function () {
            var cur = { frame: S.style.frame || presetOf().frame, pal: S.style.pal || presetOf().pal, title: S.style.font_title || presetOf().title };
            var look = window.CertSmart.nextLook(cur);
            var pal = window.CertTemplates.palette(look.pal);
            S.style.frame = look.frame;
            if (pal) { S.style.pal = pal.id; S.style.ink = pal.ink; S.style.accent = pal.accent; S.style.paper = pal.paper; }
            S.style.font_title = look.title; S.style.font_name = look.name; S.style.font_body = look.body;
            syncAll(); commitAfter('shuffle');
            toast(t('shuffled'), 'info');
        });
        $('cm-checkBtn').addEventListener('click', function () {
            $$('.cm-tab').forEach(function (x) { if (x.getAttribute('data-tab') === 'print') x.click(); });
            var box = $('cm-checkList');
            if (box) box.scrollIntoView({ block: 'nearest' });
            fun('preflight_open', {});
        });
        $('cm-pdf').addEventListener('click', exportPdf);
    }

    function wireDesign() {
        bindColor('cm-ink', 'ink'); bindColor('cm-accent', 'accent'); bindColor('cm-paper', 'paper');
        $('cm-paperNone').addEventListener('change', function () {
            S.style.paper = this.checked ? 'none' : $('cm-paper').value;
            commitAfter('paper');
        });
        ['fTitle:font_title', 'fName:font_name', 'fBody:font_body'].forEach(function (pair) {
            var p = pair.split(':');
            $('cm-' + p[0]).addEventListener('change', function () { S.style[p[1]] = this.value; commitAfter('font'); });
        });
        $('cm-wm').addEventListener('change', function () { S.style.wm = this.checked; commitAfter('wm'); });
        $('cm-wmSize').addEventListener('input', function () { S.style.wmSize = +this.value; schedule(); });
        $('cm-wmSize').addEventListener('change', function () { editor.commit('wm_size'); });
        $('cm-wmOp').addEventListener('input', function () { S.style.wmOp = +this.value; schedule(); });
        $('cm-wmOp').addEventListener('change', function () { editor.commit('wm_op'); });
        $('cm-valign').addEventListener('change', function () { S.style.vAlign = this.value; commitAfter('valign'); });
        $('cm-pad').addEventListener('input', function () { S.style.pad = +this.value; schedule(); });
        $('cm-pad').addEventListener('change', function () { editor.commit('pad'); });
        $('cm-resetStyle').addEventListener('click', function () {
            S.style = {}; S.nudge = {}; S.blockStyle = {};
            syncAll(); commitAfter('reset_style');
        });
        function bindColor(id, key) {
            $(id).addEventListener('input', function () { S.style[key] = this.value; schedule(); });
            $(id).addEventListener('change', function () { editor.commit('colour'); });
        }
    }

    function wireText() {
        $$('[data-txt]').forEach(function (el) {
            el.addEventListener('focus', function () { lastFocused = el; });
            el.addEventListener('input', function () {
                S.texts[el.getAttribute('data-txt')] = el.value;
                schedule();
            });
            el.addEventListener('change', function () { editor.commit('type'); });
        });
        $('cm-dateStyle').addEventListener('change', function () {
            S.texts.date = dateNow(this.value); syncTextInputs(); commitAfter('date_style');
        });
        $('cm-dateToday').addEventListener('click', function () {
            S.texts.date = dateNow($('cm-dateStyle').value); syncTextInputs(); commitAfter('date_today');
        });

        /* Paste-and-parse: the field people arrive with is a whole certificate. */
        $('cm-pasteBtn').addEventListener('click', function () {
            var box = $('cm-pasteBox');
            box.hidden = !box.hidden;
            if (!box.hidden) { $('cm-pasteText').focus(); fun('paste_open', {}); }
        });
        $('cm-pasteCancel').addEventListener('click', function () {
            $('cm-pasteBox').hidden = true; $('cm-pasteResult').innerHTML = '';
        });
        $('cm-pasteApply').addEventListener('click', function () {
            var text = $('cm-pasteText').value;
            var r = window.CertSmart.parseCertificate(text);
            var box = $('cm-pasteResult');
            var n = Object.keys(r.fields).length;
            if (!n || r.confidence < 0.35) {
                box.innerHTML = '<div class="cm-note cm-note-warn">' + esc(t('paste_fail')) + '</div>';
                fun('paste_apply', { ok: 0, conf: Math.round(r.confidence * 100) });
                return;
            }
            Object.keys(r.fields).forEach(function (k) { S.texts[k] = r.fields[k]; });
            syncTextInputs();
            commitAfter('paste_apply');
            box.innerHTML = '<div class="cm-note cm-note-info">' + esc(t('paste_ok', { n: n })) + '</div>';
            $('cm-pasteBox').hidden = true;
            $('cm-pasteText').value = '';
            fun('paste_apply', { ok: 1, fields: n, conf: Math.round(r.confidence * 100) });
        });
    }

    function wireMarks() {
        $$('[data-addov]').forEach(function (b) {
            b.addEventListener('click', function () { addOverlay(b.getAttribute('data-addov')); });
        });
        $('cm-ovFile').addEventListener('change', function () {
            readOverlayImage(this.files && this.files[0]);
            /* Clearing the input is what lets the SAME file be picked twice —
               without it the second pick fires no change event at all. */
            this.value = '';
        });
    }

    function wireList() {
        $('cm-batchOn').addEventListener('change', function () {
            S.batch.on = this.checked;
            /* Remembered so reparse() does not switch it straight back on. Set
               only for a human's click: the blank-form path flips this box for
               its own reasons and must not be recorded as the user's choice. */
            if (!programmaticBatch) S.batch.userOff = !this.checked;
            $('cm-zip').hidden = !this.checked;
            exportLabel();
            if (this.checked) {
                var f = document.querySelector('[data-txt="name"]');
                /* The name field becomes a TEMPLATE. Doing it visibly beats a
                   hidden rule that silently ignores whatever they typed. */
                if (f && f.value.indexOf('{') < 0) { f.value = '{фио}'; S.texts.name = f.value; }
                if (S.blankForm) { S.blankForm = false; $('cm-blankForm').checked = false; }
                reparse();
            }
            commitAfter('batch');
        });
        $('cm-list').addEventListener('input', function () { S.batch.raw = this.value; reparse(); schedule(); });
        $('cm-list').addEventListener('change', function () { editor.commit('list'); });
        $('cm-bindCols').addEventListener('click', function () {
            var n = bindColumns();
            toast(n ? t('bound_n', { n: n }) : t('bound_none'), n ? 'success' : 'warning');
            fun('bind_cols', { n: n });
        });
        $('cm-fixCaps').addEventListener('change', function () {
            S.batch.fixCaps = this.checked; paintRows(); commitAfter('fix_caps');
        });
        $('cm-registry').addEventListener('click', exportRegistry);
        $('cm-hasHeader').addEventListener('change', function () {
            S.batch.headerTouched = 1;          /* a human decided; stop guessing */
            reparse(); schedule();
        });
        $('cm-listFile').addEventListener('change', function () {
            var f = this.files && this.files[0];
            this.value = '';
            if (!f) return;
            window.CertData.readTextFile(f).then(function (txt) {
                S.batch.raw = txt;
                S.batch.userOff = false;
                $('cm-list').value = txt;
                if (!$('cm-batchOn').checked) { $('cm-batchOn').checked = true; $('cm-batchOn').dispatchEvent(new Event('change')); }
                reparse(); commitAfter('list_import');
                fun('list_import', { rows: lastRows ? lastRows.rows.length : 0 });
            }).catch(function (e) { err(e, 'list_import'); });
        });
        $('cm-case').addEventListener('change', function () {
            S.batch.kase = this.value; paintCaseExample(); paintRows(); commitAfter('case');
            if (_fioDead && this.value !== 'nomn') toast(t('decl_off'), 'warning');
        });
        $('cm-numOn').addEventListener('change', function () {
            S.batch.num = this.checked; $('cm-numOpts').hidden = !this.checked; commitAfter('numbering');
        });
        ['numPrefix', 'numStart', 'numPad'].forEach(function (k) {
            $('cm-' + k).addEventListener('input', function () { S.batch[k] = this.value; schedule(); });
        });
        $('cm-prev').addEventListener('click', function () { S.batch.index = Math.max(0, S.batch.index - 1); paintRows(); schedule(); });
        $('cm-next').addEventListener('click', function () {
            S.batch.index = Math.min((lastRows ? lastRows.rows.length : 1) - 1, S.batch.index + 1);
            paintRows(); schedule();
        });
    }

    var programmaticBatch = false;

    function wirePrint() {
        $('cm-blankForm').addEventListener('change', function () {
            S.blankForm = this.checked;
            /* A blank form has no recipients, so the list is meaningless on it —
               and both on would print N identical empty sheets against N units
               of the batch cap. */
            if (this.checked && S.batch.on) {
                programmaticBatch = true;
                $('cm-batchOn').checked = false;
                $('cm-batchOn').dispatchEvent(new Event('change'));
                programmaticBatch = false;
            } else if (!this.checked) {
                reparse();          /* blank form off → the list speaks again */
            }
            commitAfter('blank_form');
        });
        $('cm-overprint').addEventListener('change', function () {
            S.overprint = this.checked;
            $('cm-overprintBox').hidden = !this.checked;
            commitAfter('overprint');
        });
        $('cm-bdFile').addEventListener('change', function () {
            var f = this.files && this.files[0];
            this.value = '';
            if (!f) return;
            var fr = new FileReader();
            fr.onload = function () { S.backdrop = fr.result; $('cm-bdClear').hidden = false; schedule(); };
            fr.readAsDataURL(f);
        });
        $('cm-bdClear').addEventListener('click', function () { S.backdrop = null; this.hidden = true; schedule(); });
        $('cm-bdOp').addEventListener('input', function () { S.backdropOp = +this.value; schedule(); });

        ['bleed', 'dpi'].forEach(function (k) {
            $('cm-' + k).addEventListener('input', function () { S.out[k] = +this.value; });
        });
        $('cm-crop').addEventListener('change', function () { S.out.crop = this.checked; });
        $('cm-ruler').addEventListener('change', function () { S.out.ruler = this.checked; });
        ['kScale:scale', 'kDx:dx', 'kDy:dy'].forEach(function (pair) {
            var p = pair.split(':');
            $('cm-' + p[0]).addEventListener('input', function () { S.out[p[1]] = +this.value || 0; });
        });
        $('cm-kApply').addEventListener('click', function () {
            var nom = +$('cm-kNominal').value, got = +$('cm-kMeasured').value;
            if (!(nom > 0) || !(got > 0)) return;
            var pct = Math.round((nom / got - 1) * 10000) / 100;
            S.out.scale = pct; $('cm-kScale').value = pct;
            toast(t('ready'), 'success');
        });
        $('cm-kReset').addEventListener('click', function () {
            S.out.scale = 0; S.out.dx = 0; S.out.dy = 0;
            $('cm-kScale').value = 0; $('cm-kDx').value = 0; $('cm-kDy').value = 0;
        });
        $('cm-pdf2').addEventListener('click', exportPdf);
        $('cm-png').addEventListener('click', exportPng);
        $('cm-zip').addEventListener('click', exportZip);
        $('cm-safe').addEventListener('change', function () { S.safe = this.checked; schedule(); });

        $('cm-save').addEventListener('click', function () {
            dl(new Blob([JSON.stringify(snapshot(), null, 1)], { type: 'application/json' }), baseName() + '.json');
            toast(t('saved'), 'success');
        });
        $('cm-loadFile').addEventListener('change', function () {
            var f = this.files && this.files[0];
            this.value = '';
            if (!f) return;
            f.text().then(function (txt) {
                if (!adopt(JSON.parse(txt))) throw new Error('bad');
                syncAll(); reparse(); redraw();
                editor.reset();
                toast(t('loaded'), 'success');
            }).catch(function () { toast(t('bad_project'), 'error'); });
        });
    }

    /* ── boot ────────────────────────────────────────────────────────────── */

    function boot() {
        if (!window.CertLayout || !window.CertFrames || !window.CertTemplates || !window.CertRender) return;
        measure = window.CertRender.measurer(cssFor);

        wireTabs(); wireHead(); wireDesign(); wireBackground(); wireText(); wireMarks(); wireList(); wirePrint();

        watchForUser();
        if (!restore()) applyPreset('gramota-classic');

        buildTypeSelect(); buildFontSelects(); buildWordings();
        syncAll();
        reparse();
        redraw();

        editor = window.CertEditor.create({
            svg: $('cm-svg'), host: $('cm-wrap'),
            getState: function () { return S; },
            getResult: function () { return lastResult; },
            cssFor: cssFor,
            fieldOf: fieldOf,
            labelOf: labelOf,
            beforeEdit: beforeEdit,
            tabOrder: function () {
                return (lastResult.blocks || []).map(function (b) { return b.id; }).filter(Boolean);
            },
            onChange: function (why) {
                /* Editing on the sheet writes straight into S.texts, so the
                   panel's inputs have to follow — there is one text, and two
                   places showing it must never disagree. */
                if (why === 'type' || why === 'undo' || why === 'redo' || why === 'edit_cancel') syncTextInputs();
                /* Undo restores the NUMBERS; the picture they describe has to be
                   rebuilt, or the sliders and the sheet disagree. */
                if (why === 'undo' || why === 'redo') { syncBgUi(); bakeSoon(); }
                schedule();
            },
            onSelect: function (id) {
                buildFloat(id);
                paintSelection(id);
                positionFloat();
                /* The hint follows the POINTER, not the screen width: a tablet
                   with a keyboard still deserves the Delete/arrows line, and a
                   phone must never be told to press keys it does not have. */
                var touch = window.matchMedia && window.matchMedia('(pointer:coarse)').matches;
                $('cm-tip').textContent = id
                    ? t(touch ? 'tip_selected_touch' : 'tip_selected', { name: labelOf(id) })
                    : t('tip_start');
                if (id) funOnce('canvas_select', { block: id });
            },
            onDragStart: function () { $('cm-float').hidden = true; },
            onDragEnd: function (id) { buildFloat(id); positionFloat(); },
            onHistory: function (h) {
                $('cm-undo').disabled = !h.canUndo;
                $('cm-redo').disabled = !h.canRedo;
            }
        });
        editor.reset();

        $('cm-selClear').addEventListener('click', function () { editor.select(null, 'panel'); });

        stage('view');

        window.CertData.initFio().then(function (ok) {
            if (ok) { if (S.batch.on) { paintRows(); schedule(); } return; }
            _fioDead = true;
            if (S.batch.on && S.batch.kase !== 'nomn') toast(t('decl_off'), 'warning');
        });

        /* Measuring against a fallback face gives line breaks that jump under
         * the user's hands a second later, so everything is laid out again once
         * the real faces resolve. */
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(function () {
                measure = window.CertRender.measurer(cssFor);
                buildPresetThumbs(); buildFrameThumbs(); buildBgThumbs(); syncBgUi(); redraw();
            });
        }
        document.addEventListener('tier-bootstrap:ready', function () {
            if (lastRows) paintBatchNote(lastRows.rows.length);
        });
        /* The sheet is sized by the stage, so the float toolbar has to follow a
           resize as well as a selection. */
        if (window.ResizeObserver) {
            new ResizeObserver(function () { sizeSheet(); positionFloat(); }).observe($('cm-wrap'));
        } else {
            window.addEventListener('resize', function () { sizeSheet(); positionFloat(); });
        }
        /* The on-screen keyboard does not resize the layout viewport, so the
           observer above never hears it. Without this the sheet keeps a
           third of a screen that is no longer there. */
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', function () { sizeSheet(); positionFloat(); });
        }
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
})();
